import { ChangeEvent, useEffect, useState } from 'react';
import { GrPowerReset } from 'react-icons/gr';
import styles from './CineFpsPopover.module.css';

const MIN_FPS = 1;
const MAX_FPS = 120;

interface CineFpsPopoverProps {
  open: boolean;
  /** Native FPS of the active/selected viewport; anchors the displayed value. */
  nativeFps: number;
  /** Global playback factor relative to native (1 = native rate). */
  cineSpeedMultiplier: number;
  onCineSpeedMultiplierChange: (multiplier: number) => void;
}

const clampFps = (fps: number) =>
  Math.min(MAX_FPS, Math.max(MIN_FPS, Math.round(fps)));

/**
 * Frame-rate control for cine playback. The slider/value reflect the
 * *effective* FPS of the selected viewport (its native FPS × the global
 * speed factor); changing it sets the global factor, which applies
 * proportionally to every visible cine based on each one's own native FPS.
 */
export function CineFpsPopover({
  open,
  nativeFps,
  cineSpeedMultiplier,
  onCineSpeedMultiplierChange,
}: CineFpsPopoverProps) {
  // Native FPS may be unknown (0) until the video loads or for non-video panes.
  const hasNativeFps = nativeFps > 0;
  const effectiveFps = hasNativeFps
    ? clampFps(nativeFps * cineSpeedMultiplier)
    : 0;

  // Local text state for the number input so it can be cleared/edited freely
  // while typing. Re-syncs whenever the effective FPS changes externally
  // (slider, Native reset, or switching the selected viewport).
  const [inputValue, setInputValue] = useState('');
  useEffect(() => {
    setInputValue(hasNativeFps ? String(effectiveFps) : '');
  }, [effectiveFps, hasNativeFps]);

  const setFps = (fps: number) => {
    if (!hasNativeFps) return;
    const calculatedMultiplier = clampFps(fps) / nativeFps;
    const clampedMultiplier = Math.max(
      0.0625,
      Math.min(calculatedMultiplier, 16)
    );
    onCineSpeedMultiplierChange(clampedMultiplier);
  };

  const handleSliderChange = (event: ChangeEvent<HTMLInputElement>) =>
    setFps(parseInt(event.target.value, 10));

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    const value = parseInt(event.target.value, 10);
    if (Number.isFinite(value)) setFps(value);
  };

  // Restore the input to the current effective FPS if left empty/invalid.
  const handleInputBlur = () =>
    setInputValue(hasNativeFps ? String(effectiveFps) : '');

  const resetToNative = () => onCineSpeedMultiplierChange(1);

  return (
    <div className={styles.popover} data-open={open}>
      <div className={styles.header}>
        <span className={styles.title}>Cine FPS</span>
        <button
          type="button"
          className={styles.nativeButton}
          onClick={resetToNative}
          aria-label="Reset to native frame rate"
          title="Reset to native frame rate"
        >
          <span>Native</span>
          <GrPowerReset />
        </button>
      </div>

      <div className={styles.sliderRow}>
        <span className={styles.bound}>{MIN_FPS}</span>
        <input
          className={styles.slider}
          type="range"
          min={MIN_FPS}
          max={MAX_FPS}
          step={1}
          value={effectiveFps}
          onChange={handleSliderChange}
          disabled={!hasNativeFps}
          aria-label="Cine frame rate"
        />
        <span className={styles.bound}>{MAX_FPS}</span>
        <input
          className={styles.valueInput}
          type="number"
          min={MIN_FPS}
          max={MAX_FPS}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          disabled={!hasNativeFps}
          aria-label="Cine frame rate value"
        />
      </div>

      <span className={styles.hint}>Based on selected viewport</span>
    </div>
  );
}
